import '@mdxeditor/editor/style.css';

import {
  codeBlockPlugin,
  headingsPlugin,
  imagePlugin,
  linkPlugin,
  listsPlugin,
  MDXEditor,
  quotePlugin,
  tablePlugin,
  thematicBreakPlugin,
} from '@mdxeditor/editor';
import classNames from 'clsx';
import { useMemo } from 'react';

import { useThemeMode } from '@/themes/ThemeModeProvider';

type Props = {
  markdown: string;
  className?: string;
};

/**
 * Read-only, chrome-free renderer for markdown authored with `VNRichtext`. Uses the same MDXEditor
 * node plugins as the editor (minus the toolbar/tabs/source editing chrome) so viewing matches
 * editing exactly, and shares the `vn-prose` styling baseline (see `vn-richtext.scss`).
 *
 * MDXEditor's `markdown` prop is applied on mount only, so we key the editor by its content to force
 * a remount when the rendered body changes (e.g. selecting a different notification).
 */
const VNRichtextDisplay = ({ markdown, className }: Props) => {
  const { themeName } = useThemeMode();
  const isDarkTheme = themeName.includes('dark');

  const plugins = useMemo(
    () => [
      headingsPlugin(),
      listsPlugin(),
      quotePlugin(),
      thematicBreakPlugin(),
      linkPlugin(),
      tablePlugin(),
      imagePlugin(),
      codeBlockPlugin({ defaultCodeBlockLanguage: '' }),
    ],
    []
  );

  return (
    <div className={classNames('vn-richtext-display', className)}>
      <MDXEditor
        key={markdown}
        className={classNames({ 'dark-theme': isDarkTheme })}
        contentEditableClassName="vn-richtext-display__content"
        markdown={markdown}
        plugins={plugins}
        readOnly
        onError={() => {
          // Swallow malformed-markdown errors so a bad body can't crash the view.
        }}
      />
    </div>
  );
};

export default VNRichtextDisplay;
